// [MCV-1889] Every side-dependent B2B string lives in this one map, so the SEEKING column stays
// byte-for-byte what the app shows today and the OFFERING column is the only thing that changes
// per side. Components read `b2bSideCopy(listingSide)` instead of branching on the side inline.
//
// Add a key here (SEEKING = today's exact string) before using a new side-dependent string in a
// component. Keys below the "other work packages" marker are filled in and verified against the
// current source so the Responses, Negotiation and Listings work can consume them without
// re-deriving the strings.

export type ListingSide = 'SEEKING' | 'OFFERING';

type SideCopy = {
  // Marketplace search (Buying tab = OFFERING listings)
  requestLaborCta: string;
  applyIntro: string;
  negotiateIntro: string;
  submitOffer: string;
  submittingOffer: string;
  retryOffer: string;
  alreadyResponded: string;
  ftesRequiredLabel: string;
  coverageHeading: string;

  // Other work packages (Responses, Negotiation, Listings wizard)
  laborHeading: string;
  fteDescription: string;
  totalRvusLabel: string;
  ftesTotalLabel: string;
  costAnalysisHeading: string;
  averagePerShift: string;
  averagePerHour: string;
  responderLabel: string;
  messageToResponder: string;
  /** Shown in place of "Providers (0)" to the buyer when the seller attached nobody; null = keep it. */
  emptyRosterNote: string | null;
  /** The pending-acceptance notes on a Received response: the other side accepted / this side did. */
  responderAcceptedNote: string;
  youAcceptedNote: string;
  listedByLabel: string;
  ownerNoun: string;
  negotiationListingParty: string;
  negotiationRespondingParty: string;
  staffingHeading: string;

  // B2B Listings: the "+ New" menu and the wizard header (DD15)
  /** What the side is called in the Side column, the wizard header and the pill. */
  sideLabel: string;
  newListingMenuLabel: string;
  newListingTitle: string;
  editListingTitle: string;
};

const SEEKING_COPY: SideCopy = {
  requestLaborCta: 'Apply',
  applyIntro:
    "Apply starts a negotiation using the listing's current terms. You can exchange counteroffers before both parties accept. Your information remains anonymous until the unmasking requirements are met.",
  negotiateIntro:
    'You are about to begin negotiating the terms of this position. You may negotiate for multiple rounds, but the final terms must be accepted by both parties. You will control final approval before agreeing to the terms and conditions.',
  submitOffer: 'Submit Offer',
  submittingOffer: 'Submitting Offer',
  retryOffer: 'Retry original offer',
  alreadyResponded: 'You have already responded to this listing.',
  ftesRequiredLabel: 'FTEs Required',
  coverageHeading: 'Coverage Requirements',

  laborHeading: 'Labor Requirements',
  fteDescription: 'Full-time equivalents needed',
  totalRvusLabel: 'Total RVUs Required',
  ftesTotalLabel: 'FTEs Needed:',
  costAnalysisHeading: 'Comprehensive Cost Analysis',
  averagePerShift: 'Average Cost Per Shift',
  averagePerHour: 'Average Cost Per Hour',
  responderLabel: 'Responder',
  messageToResponder: 'Message to responder',
  emptyRosterNote: null,
  responderAcceptedNote:
    'The responder accepted the current offer. Review and accept it to complete the agreement.',
  youAcceptedNote:
    'You accepted the current offer. The responder must confirm before the agreement is complete.',
  listedByLabel: 'Listed by',
  ownerNoun: 'the listing owner',
  negotiationListingParty: 'Listing Employer',
  negotiationRespondingParty: 'Responding Group',
  staffingHeading: 'Staffing Requirements',

  sideLabel: 'Buying',
  newListingMenuLabel: 'Buying labor: post what you need',
  newListingTitle: 'New buying listing',
  editListingTitle: 'Edit buying listing',
};

const OFFERING_COPY: SideCopy = {
  requestLaborCta: 'Request this labor',
  applyIntro: 'Review and edit the proposed terms before sending.',
  negotiateIntro: 'Review and edit the proposed terms before sending.',
  submitOffer: 'Send request',
  submittingOffer: 'Sending request',
  retryOffer: 'Retry original request',
  alreadyResponded: 'You have already requested this labor.',
  ftesRequiredLabel: 'FTEs Available',
  coverageHeading: 'Coverage Offered',

  laborHeading: 'Labor Available',
  fteDescription: 'Full-time equivalents available',
  totalRvusLabel: 'Total RVUs Available',
  ftesTotalLabel: 'FTEs Available:',
  costAnalysisHeading: 'Comprehensive Pricing Analysis',
  averagePerShift: 'Average Price Per Shift',
  averagePerHour: 'Average Price Per Hour',
  responderLabel: 'Requesting employer',
  messageToResponder: 'Message to requesting employer',
  emptyRosterNote: "The seller hasn't attached providers yet",
  responderAcceptedNote:
    'The requesting employer accepted the current offer. Review and accept it to complete the agreement.',
  youAcceptedNote:
    'You accepted the current offer. The requesting employer must confirm before the agreement is complete.',
  listedByLabel: 'Offered by',
  ownerNoun: 'the supplier',
  negotiationListingParty: 'Supplying Group',
  negotiationRespondingParty: 'Requesting Employer',
  staffingHeading: 'Staffing Offered',

  sideLabel: 'Selling',
  newListingMenuLabel: 'Selling labor: offer your providers',
  newListingTitle: 'New selling listing',
  editListingTitle: 'Edit selling listing',
};

export const b2bSideCopy = (side: ListingSide | null | undefined): SideCopy =>
  side === 'OFFERING' ? OFFERING_COPY : SEEKING_COPY;
